import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useYouTubePlayer } from "../hooks/useYouTubePlayer";
import { usePresence } from "../hooks/usePresence";
import { useAnalytics, trackMetaPixel } from "../hooks/useAnalytics";
import { useSleepControls } from "../hooks/useSleepControls";
import { SceneLayers } from "../components/release/SceneLayers";
import { Header } from "../components/release/Header";
import { LiveBadge } from "../components/release/LiveBadge";
import { PlayerBar } from "../components/release/PlayerBar";
import { ConsentBanner } from "../components/release/ConsentBanner";
import { InAppBrowserNotice } from "../components/release/InAppBrowserNotice";
import { isInAppBrowser, isAndroid, buildAndroidChromeIntentUrl } from "../lib/detectInAppBrowser";
import { RELEASE } from "../constants/testIds";
import "../styles/release-scene.css";

const THEME_VAR_MAP = {
  moon: "--moon", moonSoft: "--moon-soft", ink: "--ink", inkDim: "--ink-dim", inkFaint: "--ink-faint",
  line: "--line", panel: "--panel",
  lanternC1: "--lantern-c1", lanternC2: "--lantern-c2",
  lanternOffC1: "--lantern-off-c1", lanternOffC2: "--lantern-off-c2",
  glowC1: "--glow-c1", glowC2: "--glow-c2",
  petalC1: "--petal-c1", petalC2: "--petal-c2",
  moonBreathC1: "--moonbreath-c1", moonBreathC2: "--moonbreath-c2",
  nightShadeBg: "--nightshade-bg", stageBg: "--stage-bg",
};

export default function ReleasePage({ slug }) {
  const [release, setRelease] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [asleep, setAsleep] = useState(false);
  const [lanternsOff, setLanternsOff] = useState([false, false]);

  useEffect(() => {
    setRelease(null);
    setNotFound(false);
    api.get(`/releases/${slug}`).then(({ data }) => setRelease(data)).catch(() => setNotFound(true));
  }, [slug]);

  const { track } = useAnalytics(slug);
  const liveCount = usePresence(slug);

  useEffect(() => {
    if (isAndroid() && isInAppBrowser()) {
      track("android_auto_redirect_attempted");
      window.location.href = buildAndroidChromeIntentUrl();
    }
  }, [track]);

  const onTrackTransition = useCallback(({ index, completed, pct }) => {
    const titles = release?.track?.titles || [];
    const title = titles.length ? titles[index % titles.length]?.t : `Track ${index + 1}`;
    track(completed ? "track_complete" : "track_skip", { index, title, pct });
  }, [release, track]);

  const yt = useYouTubePlayer(`yt-${slug}`, release?.track?.youtubePlaylistId, onTrackTransition);

  useEffect(() => {
    if (release) track("page_view");
  }, [release, track]);

  useEffect(() => {
    if (release && isInAppBrowser()) track("inapp_browser_detected");
  }, [release, track]);

  useEffect(() => {
    const onConsentChange = (e) => {
      if (e.detail === "granted" && isInAppBrowser()) track("inapp_browser_detected");
    };
    window.addEventListener("ng-consent-changed", onConsentChange);
    return () => window.removeEventListener("ng-consent-changed", onConsentChange);
  }, [track]);

  useEffect(() => {
    if (!yt.playing) return;
    const interval = setInterval(() => track("listen_heartbeat"), 15000);
    return () => clearInterval(interval);
  }, [yt.playing, track]);

  const { timerLabel, fadeLabel, onTimerClick, onFadeClick } = useSleepControls({
    playing: yt.playing, ready: yt.ready, setVolume: yt.setVolume, pause: yt.pause,
    asleep, setAsleep, track,
  });

  const onLights = useCallback(() => {
    setAsleep((prev) => {
      const next = !prev;
      track("light_switch", { asleep: next });
      return next;
    });
  }, [track]);

  const onLanternClick = useCallback((idx) => {
    setLanternsOff((prev) => {
      const next = [...prev];
      next[idx] = !next[idx];
      track("lantern_toggle", { off: next[idx] });
      return next;
    });
  }, [track]);

  const onFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen().catch(() => {});
    track("fullscreen_toggle");
  }, [track]);

  const onPlayPause = useCallback(() => {
    if (!yt.ready) {
      track("play_tap_not_ready", { inApp: isInAppBrowser() });
      return;
    }
    if (yt.playing) {
      yt.pause();
      track("pause", { index: yt.playlistIndex });
    } else {
      yt.play();
      track("play", { index: yt.playlistIndex });
      trackMetaPixel("PlayButtonClick", { release: slug });
    }
  }, [yt, track, slug]);

  const onNext = useCallback(() => { if (yt.ready) yt.next(); }, [yt]);
  const onPrev = useCallback(() => { if (yt.ready) yt.prev(); }, [yt]);
  const onOutbound = useCallback((dest) => track("outbound", { dest }), [track]);

  useEffect(() => {
    const handler = (e) => {
      if (e.code === "Space" && !e.target.matches("input,textarea")) {
        e.preventDefault();
        onPlayPause();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onPlayPause]);

  if (notFound) {
    return <div className="min-h-screen bg-[#05071a] text-[#e6e9f8] flex items-center justify-center">Release not found.</div>;
  }
  if (!release) {
    return <div className="min-h-screen bg-[#05071a]" />;
  }

  const themeStyle = {};
  Object.entries(release.theme || {}).forEach(([key, val]) => {
    const cssVar = THEME_VAR_MAP[key];
    if (cssVar) themeStyle[cssVar] = val;
  });

  return (
    <div data-testid={RELEASE.page} className={`ng-page${asleep ? " asleep" : ""}`} style={themeStyle}>
      <SceneLayers art={release.art} lanternsOff={lanternsOff} onLanternClick={onLanternClick} />
      <div id="nightShade"></div>
      <div id="scrims"></div>
      <div id="engine"><div id={`yt-${slug}`}></div></div>
      <div id="app">
        <Header
          sleepControls={release.featureFlags?.sleepControls}
          lightsLabel={asleep ? "LIGHTS ON" : "LIGHTS OFF"}
          timerLabel={timerLabel}
          fadeLabel={fadeLabel}
          onLights={onLights}
          onTimer={onTimerClick}
          onFade={onFadeClick}
          onFullscreen={onFullscreen}
        />
        <LiveBadge count={liveCount} />
        <div className="hero"><h1 data-testid={RELEASE.heroTitle}>{release.title}</h1></div>
        <div className="spacer"></div>
        <InAppBrowserNotice />
        <PlayerBar
          titles={release.track.titles}
          playlistIndex={yt.playlistIndex}
          videoId={yt.videoId}
          playing={yt.playing}
          currentTime={yt.currentTime}
          duration={yt.duration}
          onPlayPause={onPlayPause}
          onNext={onNext}
          onPrev={onPrev}
          onSeek={yt.seekTo}
          spotifyUrl={release.track.spotifyUrl}
          ytMusicUrl={release.track.ytMusicUrl}
          onOutbound={onOutbound}
        />
      </div>
      <ConsentBanner />
    </div>
  );
}
